import React, { useState, useMemo, KeyboardEvent, useEffect } from 'react'; // FIX: Added KeyboardEvent
import { useUser } from '../../contextManager/user.context';
import { ProfilePatch } from '../../hooks/auth.service';
import { FaTimes } from 'react-icons/fa';

// --- LAYOUT & BUTTONS ---
import { ProfileDescriptionCard, CardHeader, CardContent, CardFooter, ParentProfileCard } from '../../styles/profile/layout.style';
import { ActionButton } from '../../styles/profile/profile.style';

// --- FIELD COMPONENTS ---
import { EditField, SubheaderContainer, SubheaderLine, SubheaderTitle } from '../../styles/profile/form.style';
import { ManualField, ManualLabel, ManualInput } from './ProfileDetails';
import { TagWrap, Tag, Remove, PreviewViewport, Grid, Cell, OverlayTextarea } from '../../styles/login.style';
import UniversityDropdown from '../register/UniversityDropdown';
import SubjectCombobox from '../common/SubjectCombobox';

// --- DATA CONSTANTS ---
import { cambridgeColleges, classificationOptions, LEVEL_OPTIONS, oxfordColleges, QualificationGroup, SUBJECT_OPTIONS, SubjectEntry } from '../../types/schemas/abc';
import { TableHintSmall } from '../register/InfoBlock';
import { TimezonePicker } from '../common/TimezonePicker';
import {
    aLevelGrades, aLevelSubjectOptions, apGrades, apSubjectOptions,
    cambridgePreUGrades, cambridgePreUSubjectOptions, gcseGrades,
    gcseSubjectOptions, hkdseGrades, ibGrades, ibSubjectOptions,
    indianSchoolCertificateGrades, irishLeavingCertGrades,
    irishLeavingCertSubjectOptions, qualificationOptions, scottishHighersGrades,
    scottishHighersSubjectOptions, singaporeGceALevelGrades
  } from '../../types/schemas/subjectsandgrades';
import StyledDropdown, { OptionGroup } from '../register/StyledDropdown';
import { Field, Label, Control, AddSection, LightButton, GroupContainer, GroupHeader, GroupTitle, EntryRow, TotalScoreContainer, TotalScoreValue, TotalScoreLabel, TotalScoreBreakdown } from '../../styles/login.style';
import { Plus, Trash2 } from 'lucide-react';
import QualificationsDropdown from '../register/QualificationDropdown';

interface ParentProfileDescriptionFormProps {
    onSave: (formData: ProfilePatch) => Promise<void>;
    onCancel: () => void;
  }
  
  export const ParentProfileDescriptionForm: React.FC<ParentProfileDescriptionFormProps> = ({ onSave, onCancel }) => {
    const { userDetails } = useUser();
    const [formData, setFormData] = useState<ProfilePatch>({
        subjects: userDetails?.subjects ?? [],
        levels: userDetails?.levels ?? [],
        expertise: userDetails?.expertise ?? {},
        description: userDetails?.description ?? '',
        timezone: userDetails?.timezone ?? '',
      });

    const [isSaving, setIsSaving] = useState(false);

    const isDirty = useMemo(() => {
        const initialData = {
          subjects: userDetails?.subjects ?? [],
          levels: userDetails?.levels ?? [],
          expertise: userDetails?.expertise ?? {},
          description: userDetails?.description ?? '',
          timezone: userDetails?.timezone ?? '',
        };
        return JSON.stringify(initialData) !== JSON.stringify(formData);
      }, [userDetails, formData]);

        const rowSubjects = formData.subjects ?? [];
        const colLevels = formData.levels ?? [];

        const descriptionWordCount = (formData.description ?? '').trim()
          ? (formData.description ?? '').trim().split(/\s+/).length
          : 0;
        const isDescriptionOverLimit = descriptionWordCount > 500;

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value } = e.target;
            const finalValue = name === 'gradYear' ? parseInt(value, 10) || 0 : value;
            setFormData(prev => ({ ...prev, [name]: finalValue }));
          };

          const handleSaveClick = async () => {
            // We will add the !isValid check back in later
            if (!isDirty || isSaving) return;
          
            setIsSaving(true);
            try {
              // Pass the entire formData object to the parent to save
              await onSave(formData);
            } catch (error) {
              console.error("Save failed:", error);
            } finally {
              setIsSaving(false);
            }
          };  

          const addSubject = (s: string) => {
            if (!s) return;
            const currentSubjects = new Set(formData.subjects ?? []);
            if (currentSubjects.has(s)) return;
            currentSubjects.add(s);
            setFormData(prev => ({ ...prev, subjects: Array.from(currentSubjects) }));
          };

          const removeSubject = (subjectToRemove: string) => {
            setFormData(prev => ({
              ...prev,
              subjects: (prev.subjects ?? []).filter(s => s !== subjectToRemove)
            }));
          };

        const addLevel = (l: string) => {
          if (!l) return;
          const currentLevels = new Set(formData.levels ?? []);
          if (currentLevels.has(l)) return;
          currentLevels.add(l);
          setFormData(prev => ({ ...prev, levels: Array.from(currentLevels) }));
        };

        const removeLevel = (levelToRemove: string) => {
          setFormData(prev => ({
            ...prev,
            levels: (prev.levels ?? []).filter(l => l !== levelToRemove)
          }));
        };
        
        
        
        
        
        const toggle = (subject: string, level: string) => {
            setFormData(prev => {
              const currentLevels = prev.expertise?.[subject] ?? [];
              const isTicked = currentLevels.includes(level);
              
              const newLevels = isTicked
                ? currentLevels.filter(l => l !== level) // If ticked, remove it
                : [...currentLevels, level];             // If not ticked, add it
        
              const newExpertise = {
                ...prev.expertise,
                [subject]: newLevels,
              };
        
              return { ...prev, expertise: newExpertise };
            });
          };
        
          // This useEffect cleans up the expertise data if a subject or level tag is removed
          useEffect(() => {
            if (!formData.subjects || !formData.levels || !formData.expertise) return;
        
            const currentSubjects = new Set(formData.subjects);
            const currentLevels = new Set(formData.levels);
            const cleanedExpertise = { ...formData.expertise };
            let hasChanged = false;
        
            for (const subject in cleanedExpertise) {
              if (!currentSubjects.has(subject)) {
                delete cleanedExpertise[subject];
                hasChanged = true;
              } else {
                const originalLevels = cleanedExpertise[subject];
                const cleanedLevels = originalLevels.filter(level => currentLevels.has(level));
                if (cleanedLevels.length !== originalLevels.length) {
                  cleanedExpertise[subject] = cleanedLevels;
                  hasChanged = true;
                }
              }
            }
        
            if (hasChanged) {
              setFormData(prev => ({ ...prev, expertise: cleanedExpertise }));
            }
          }, [formData.subjects, formData.levels]);


          return (
              <>
                <CardHeader>Edit About</CardHeader>
                <form
                  onSubmit={handleSaveClick} 
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flex: '1 1 0%',
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                 > 
                <CardContent>
                <ManualField>
              <ManualLabel>Full Name</ManualLabel>
              <ManualInput value={userDetails?.name || ''} disabled />
            </ManualField>
            <ManualField>
              <ManualLabel>Email Address</ManualLabel>
              <ManualInput value={userDetails?.email || ''} disabled />
            </ManualField>
            <ManualField>
              <ManualLabel>Timezone</ManualLabel>
              <TimezonePicker
                value={formData.timezone || ''}
                onChange={(tz) => setFormData(prev => ({ ...prev, timezone: tz }))}
              />
            </ManualField>

                <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>About You</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>

        <ManualField>
          <ManualLabel>About You (500 words max)</ManualLabel>

          <OverlayTextarea
            as="textarea"
            rows={4}
            name="description"
            value={formData.description || ''}
            onChange={handleInputChange}
            placeholder="Tell tutors about your child and what you're looking for..."
          />
          <small style={{ alignSelf: 'flex-end', color: isDescriptionOverLimit ? '#dc2626' : undefined }}>
            {descriptionWordCount}/500 words
          </small>
        </ManualField>

        <SubheaderContainer>
          <SubheaderLine />
          <SubheaderTitle>Subjects</SubheaderTitle>
          <SubheaderLine />
        </SubheaderContainer>


        <ManualField>
          <ManualLabel>Subjects you need</ManualLabel>
          <SubjectCombobox
            options={SUBJECT_OPTIONS}
            mode="multi"
            exclude={formData.subjects ?? []}
            onSelect={addSubject}
            placeholder="Search or select a subject…"
          />
          <TagWrap>
            {/* FIX: Read from formData.subjects, with a fallback */}
            {(formData.subjects ?? []).map(s => (
                <Tag key={s} title={s}>
                {s}
                {/* FIX: Hooked up the new removeSubject function */}
                <Remove type="button" onClick={() => removeSubject(s)}>
                    <FaTimes size={10} />
                </Remove>
                </Tag>
            ))}
          </TagWrap>
        </ManualField>

        <ManualField>
            <ManualLabel>Levels you need</ManualLabel>
            <SubjectCombobox
              options={LEVEL_OPTIONS}
              mode="multi"
              exclude={formData.levels ?? []}
              onSelect={addLevel}
              placeholder="Search or select a level…"
            />
            <TagWrap>
                {(formData.levels ?? []).map(l => (
                <Tag key={l} title={l}>
                    {l}
                    <Remove type="button" onClick={() => removeLevel(l)}>
                    <FaTimes size={10} />
                    </Remove>
                </Tag>
                ))}
            </TagWrap>
        </ManualField>

        {rowSubjects.length && colLevels.length ? (
        <>
        <TableHintSmall css={{ margin: '1rem 0 0.75rem' }}>
          You can un-tick any subject/level combination you don’t need. Just click the ✓ in the table.
        </TableHintSmall>
        <PreviewViewport>
  
        <Grid
        css={{
            gridTemplateColumns: `minmax(140px,1fr) repeat(${colLevels.length}, minmax(110px,1fr))`,
        }}
        >

            {/* blank corner */}            <Cell header />

            {/* column headers = levels */} 
            {colLevels.map((level) => (
                <Cell header key={`c-${level}`}>{level}</Cell>
            ))}

            {/* rows = subjects */} 
            {rowSubjects.map((subject) => (
        <React.Fragment key={`r-${subject}`}>
            <Cell header>{subject}</Cell>

            {colLevels.map((level) => {
            const key = `${subject}|||${level}`;
            const isTicked = !!formData.expertise?.[subject]?.includes(level);

            return (
                <Cell
                key={key}
                ticked={isTicked}
                css={{ cursor: 'pointer' }}
                onClick={() => toggle(subject, level)}
                >
                {isTicked ? '✓' : ''}
                </Cell>
            );
            })}
        </React.Fragment>
        ))}
            </Grid>
        </PreviewViewport>
        </>
        ) : null}

                </CardContent>
                <CardFooter>
        <ActionButton type="button" intent="secondary" onClick={onCancel}>
          Cancel
        </ActionButton>
        <ActionButton
          type="button"
          intent="primary"
          onClick={handleSaveClick}
          disabled={!isDirty || isSaving || isDescriptionOverLimit}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </ActionButton>
      </CardFooter>
      </form>
      </>

          )
  }
